import * as React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { addUserToUnit } from '@/api/accounts';
import { getAllUnits } from '@/api/units';
import useUserTitles from '@/hooks/use-user-titles';

const schema = zod.object({
  unit_id: zod.string().min(1, { message: 'Unit is required' }),
  role_name: zod.string().min(1, { message: 'Role is required' }),
  user_title_id: zod.string().optional(),
});

type Values = zod.infer<typeof schema>;

interface AddUnitModalProps {
  accountId: string;
  userId: string;
  communityId: string | number;
  existingUnitIds: string[];
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const unitRoles = [
  { value: 'unit_owner', label: 'Unit Owner' },
  { value: 'unit_tenant', label: 'Unit Tenant' },
  { value: 'unit_resident', label: 'Unit Resident' },
];

export function AddUnitModal({
  accountId,
  userId,
  communityId,
  existingUnitIds,
  open,
  onClose,
  onSuccess,
}: AddUnitModalProps): React.JSX.Element {
  const { userTitles } = useUserTitles();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: {
      unit_id: '',
      role_name: '',
      user_title_id: '',
    },
  });

  const { data: unitsData } = useQuery({
    queryKey: ['units', 'all'],
    queryFn: () => getAllUnits({}),
  });

  const availableUnits = React.useMemo(() => {
    if (!unitsData?.units) return [];
    return unitsData.units.filter(
      (unit) =>
        String(unit.community_id) === String(communityId) &&
        !existingUnitIds.includes(unit.id)
    );
  }, [unitsData, existingUnitIds, communityId]);

  const mutation = useMutation({
    mutationFn: (data: {
      unit_id: string;
      role_name: string;
      user_title_id?: string;
      move_in_date?: string;
      move_out_date?: string;
    }) => addUserToUnit(accountId, userId, data),
    onSuccess: () => {
      toast.success('User added to unit successfully');
      reset({
        unit_id: '',
        role_name: '',
        user_title_id: '',
      });
      onSuccess();
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to add user to unit');
    },
  });

  const onSubmit = React.useCallback(
    async (values: Values): Promise<void> => {
      mutation.mutate({
        unit_id: values.unit_id,
        role_name: values.role_name,
        user_title_id: values.user_title_id || undefined,
      });
    },
    [mutation]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={open}
      PaperProps={{
        component: 'form',
        onSubmit: handleSubmit(onSubmit),
      }}
    >
      <DialogTitle>Add User to Unit</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          <Controller
            control={control}
            name="unit_id"
            render={({ field }) => (
              <FormControl error={Boolean(errors.unit_id)} fullWidth>
                <InputLabel>Unit</InputLabel>
                <Select {...field} label="Unit">
                  {availableUnits.map((unit) => (
                    <MenuItem key={unit.id} value={String(unit.id)}>
                      {unit.unit_number ? `Unit ${unit.unit_number}` : (unit as any).address || 'Unit (no number)'}
                    </MenuItem>
                  ))}
                </Select>
                {errors.unit_id && (
                  <FormHelperText>{errors.unit_id.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="role_name"
            render={({ field }) => (
              <FormControl error={Boolean(errors.role_name)} fullWidth>
                <InputLabel>Role</InputLabel>
                <Select {...field} label="Role">
                  {unitRoles.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {role.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.role_name && (
                  <FormHelperText>{errors.role_name.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="user_title_id"
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Title (optional)</InputLabel>
                <Select {...field} label="Title (optional)">
                  <MenuItem value="">None</MenuItem>
                  {(userTitles ?? []).map((title) => (
                    <MenuItem key={title.id} value={String(title.id)}>
                      {title.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />


        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button disabled={mutation.isPending} type="submit" variant="contained">
          {mutation.isPending ? 'Adding...' : 'Add to Unit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
